import { MENTION_NOTIFICATION_TYPES } from "@/lib/constants";
import {
  JobPriority,
  NotificationExcellentCandidatePayload,
  NotificationResponse,
  NotificationType,
} from "@/types";

// Loudest priority first; jobs with no priority set sort last.
const jobPriorityRank: Record<JobPriority, number> = {
  [JobPriority.HIGH]: 0,
  [JobPriority.MEDIUM]: 1,
  [JobPriority.LOW]: 2,
  [JobPriority.BACKBURNERED]: 3,
};
const NO_PRIORITY_RANK = Object.keys(jobPriorityRank).length;

// Notifications created before the score shipped in the payload were only ever
// sent for excellent candidates, so they bucket that way.
const DEFAULT_TOP_CANDIDATE_SCORE = 5;
const EXCELLENT_SCORE = 5;

export const topCandidates = {
  // Whether a notification is about a five-star candidate. EXCELLENT_CANDIDATE
  // also fires at four stars on non-production jobs, so the score decides.
  isExcellent: (notification: NotificationResponse): boolean => {
    if (notification.type !== NotificationType.EXCELLENT_CANDIDATE)
      return false;
    const { score } =
      notification.payload as NotificationExcellentCandidatePayload;
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return (score || DEFAULT_TOP_CANDIDATE_SCORE) >= EXCELLENT_SCORE;
  },

  // The job's current priority, which guac reads live — it changes daily, so a
  // value snapshotted when the notification fired would go stale.
  priority: (notification: NotificationResponse): JobPriority | null => {
    if (
      notification.type !== NotificationType.EXCELLENT_CANDIDATE &&
      notification.type !== NotificationType.INTERNAL_RECOMMENDATION &&
      notification.type !== NotificationType.ACTIVE_CANDIDATE_FINISHED_STEVIE &&
      notification.type !== NotificationType.CANDIDATE_RESURRECTED_AFTER_STEVIE
    ) {
      return null;
    }
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    return notification.jobPriority || null;
  },

  // Splits a list into the sections the slideout shows. Anything that isn't a
  // candidate or a mention falls into `other` rather than disappearing.
  group: (notifications: NotificationResponse[]) => {
    const excellent: NotificationResponse[] = [];
    const finishedStevie: NotificationResponse[] = [];
    const great: NotificationResponse[] = [];
    const recommendations: NotificationResponse[] = [];
    const mentions: NotificationResponse[] = [];
    const other: NotificationResponse[] = [];

    for (const notification of notifications) {
      if (
        notification.type ===
          NotificationType.ACTIVE_CANDIDATE_FINISHED_STEVIE ||
        notification.type ===
          NotificationType.CANDIDATE_RESURRECTED_AFTER_STEVIE
      ) {
        finishedStevie.push(notification);
        continue;
      }
      if (notification.type === NotificationType.INTERNAL_RECOMMENDATION) {
        recommendations.push(notification);
        continue;
      }
      if (MENTION_NOTIFICATION_TYPES.includes(notification.type)) {
        mentions.push(notification);
        continue;
      }
      if (notification.type !== NotificationType.EXCELLENT_CANDIDATE) {
        other.push(notification);
        continue;
      }

      if (topCandidates.isExcellent(notification)) {
        excellent.push(notification);
      } else {
        great.push(notification);
      }
    }

    return {
      excellent: topCandidates.sort(excellent),
      finishedStevie: topCandidates.sort(finishedStevie),
      great: topCandidates.sort(great),
      recommendations: topCandidates.sort(recommendations),
      mentions: topCandidates.sort(mentions),
      other: topCandidates.sort(other),
    };
  },

  // Highest job priority first, newest first within a priority.
  sort: (notifications: NotificationResponse[]): NotificationResponse[] => {
    const rank = (notification: NotificationResponse) => {
      const priority = topCandidates.priority(notification);
      return priority ? jobPriorityRank[priority] : NO_PRIORITY_RANK;
    };

    return [...notifications].sort((a, b) => {
      const rankDifference = rank(a) - rank(b);
      if (rankDifference !== 0) return rankDifference;
      return b.createdAt.localeCompare(a.createdAt);
    });
  },
};
